import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  MenuItem, TextField, Checkbox, Alert, Button,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { providersAPI, regionsAPI, addonsAPI, defaultAddonsAPI, environmentsAPI } from '../../services/api';
import { Provider, Region, Addon, DefaultAddon, Environment } from '../../types';

interface ChangeEntry {
  applyToVMs: boolean;
  applyToDatabaseVMs: boolean;
}

const DefaultAddons = () => {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [addons, setAddons] = useState<Addon[]>([]);
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<number | string>('');
  const [selectedRegion, setSelectedRegion] = useState<number | string>('');
  const [defaultAddons, setDefaultAddons] = useState<DefaultAddon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [changes, setChanges] = useState<Record<string, ChangeEntry>>({});

  useEffect(() => { fetchInitialData(); }, []);

  useEffect(() => {
    if (selectedProvider) { fetchRegions(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProvider]);

  useEffect(() => {
    if (selectedProvider && selectedRegion) { fetchDefaultAddons(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProvider, selectedRegion]);

  const fetchInitialData = async () => {
    try {
      const [providersRes, addonsRes, environmentsRes] = await Promise.all([providersAPI.getAll(), addonsAPI.getAll(), environmentsAPI.getAll()]);
      setProviders(providersRes.data.filter((p: Provider) => p.IsActive));
      setAddons(addonsRes.data.filter((a: Addon) => a.IsActive));
      setEnvironments(environmentsRes.data);
      if (providersRes.data.length > 0) { setSelectedProvider(providersRes.data[0].ProviderId); }
    } catch (err) { setError('Failed to load data'); } finally { setLoading(false); }
  };

  const fetchRegions = useCallback(async () => {
    try {
      const regionsRes = await regionsAPI.getAll(Number(selectedProvider));
      const activeRegions = regionsRes.data.filter((r: Region) => r.IsActive);
      setRegions(activeRegions);
      if (activeRegions.length > 0) { setSelectedRegion(activeRegions[0].RegionId); }
    } catch (err) { setError('Failed to load regions'); }
  }, [selectedProvider]);

  const fetchDefaultAddons = useCallback(async () => {
    try {
      const response = await defaultAddonsAPI.getAll(Number(selectedProvider), Number(selectedRegion));
      setDefaultAddons(response.data);
      setChanges({});
    } catch (err) { setError('Failed to load default addons'); }
  }, [selectedProvider, selectedRegion]);

  const getConfig = (addonId: number, environmentId: number, field: 'applyToVMs' | 'applyToDatabaseVMs'): boolean => {
    const changeKey = `${addonId}-${environmentId}`;
    if (changes[changeKey]?.[field] !== undefined) { return changes[changeKey]![field]; }
    const existing = defaultAddons.find(
      da => da.AddonId === addonId && da.ProviderId === Number(selectedProvider) && da.RegionId === Number(selectedRegion) && da.EnvironmentId === environmentId
    );
    return existing ? !!(existing[field === 'applyToVMs' ? 'ApplyToVMs' : 'ApplyToDatabaseVMs']) : false;
  };

  const handleToggle = (addonId: number, environmentId: number, field: 'applyToVMs' | 'applyToDatabaseVMs') => {
    const changeKey = `${addonId}-${environmentId}`;
    const currentValue = getConfig(addonId, environmentId, field);
    setChanges({
      ...changes,
      [changeKey]: {
        ...(changes[changeKey] || {}),
        applyToVMs: field === 'applyToVMs' ? !currentValue : getConfig(addonId, environmentId, 'applyToVMs'),
        applyToDatabaseVMs: field === 'applyToDatabaseVMs' ? !currentValue : getConfig(addonId, environmentId, 'applyToDatabaseVMs'),
      },
    });
  };

  const handleSaveChanges = async () => {
    try {
      const updates = Object.entries(changes).map(([key, value]) => {
        const [addonId, environmentId] = key.split('-').map(Number);
        return { addonId, providerId: Number(selectedProvider), regionId: Number(selectedRegion), environmentId, applyToVMs: value.applyToVMs, applyToDatabaseVMs: value.applyToDatabaseVMs };
      });
      await defaultAddonsAPI.batchSave(updates);
      setSuccess('Default addons updated successfully');
      setChanges({});
      fetchDefaultAddons();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError('Failed to update default addons'); }
  };

  if (loading) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" gutterBottom>Default Addons</Typography>
        <Typography variant="body2" color="text.secondary">Configure which addons are automatically applied to VMs and Database VMs per environment</Typography>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField select label="Provider" value={selectedProvider} onChange={(e) => setSelectedProvider(e.target.value)} sx={{ minWidth: 200 }}>
            {providers.map((provider) => (<MenuItem key={provider.ProviderId} value={provider.ProviderId}>{provider.ProviderName}</MenuItem>))}
          </TextField>
          <TextField select label="Region" value={selectedRegion} onChange={(e) => setSelectedRegion(e.target.value)} disabled={!selectedProvider} sx={{ minWidth: 200 }}>
            {regions.map((region) => (<MenuItem key={region.RegionId} value={region.RegionId}>{region.RegionName}</MenuItem>))}
          </TextField>
          <Button variant="contained" color="secondary" size="small" startIcon={<SaveIcon />} onClick={handleSaveChanges} disabled={Object.keys(changes).length === 0} sx={{ ml: 'auto' }}>
            Save Changes ({Object.keys(changes).length})
          </Button>
        </Box>
        {selectedProvider && selectedRegion && environments.length > 0 && (
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 180 }}>Addon Name</TableCell>
                  {environments.map((env) => (
                    <React.Fragment key={env.EnvironmentId}>
                      <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 90 }}>{env.EnvironmentCode.toUpperCase()}<br />VMs</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 90 }}>{env.EnvironmentCode.toUpperCase()}<br />DBs</TableCell>
                    </React.Fragment>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {addons.map((addon) => (
                  <TableRow key={addon.AddonId} hover>
                    <TableCell>
                      {addon.AddonName}
                      {addon.Description && (<Typography variant="caption" display="block" color="text.secondary">{addon.Description}</Typography>)}
                    </TableCell>
                    {environments.map((env) => (
                      <React.Fragment key={`${addon.AddonId}-${env.EnvironmentId}`}>
                        <TableCell align="center"><Checkbox size="small" checked={getConfig(addon.AddonId, env.EnvironmentId, 'applyToVMs')} onChange={() => handleToggle(addon.AddonId, env.EnvironmentId, 'applyToVMs')} /></TableCell>
                        <TableCell align="center"><Checkbox size="small" checked={getConfig(addon.AddonId, env.EnvironmentId, 'applyToDatabaseVMs')} onChange={() => handleToggle(addon.AddonId, env.EnvironmentId, 'applyToDatabaseVMs')} /></TableCell>
                      </React.Fragment>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      {selectedProvider && selectedRegion && (
        <Alert severity="info">
          <Typography variant="body2">
            <strong>How it works:</strong><br />
            • Enabled addons are automatically included in VM/Database VM quotes for the selected provider and region<br />
            • Each environment (PROD, DEV, etc.) can have different addon configurations<br />
            • <strong>VMs column:</strong> Addon is added to regular VM line items<br />
            • <strong>DBs column:</strong> Addon is added to SQL Server on VM line items<br />
            • The costs are included in line item totals and shown as "includes Addon1, Addon2, etc."<br />
            • Configure environment discount multipliers on the Environments page
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default DefaultAddons;
